import { Injectable, signal } from "@angular/core";
import { ColumnList, ColumnPreference, SelectedColumnPreferences } from "src/app/shared/models/column-preference";
import { DeleteListPaylaod, UserPreferencesService } from "src/app/shared/services/user-preferences/user-preferences.service";
import { DataRepositoryService } from "src/app/shared/services/data-repository/data-repository.service";
import { UserPreferenceEnum } from "../../setting/setting.component";
import { ErrorMessage } from "src/app/shared/entity/error.const";
import { PreferenceResponseModel, SelectedPreferencesENUM } from "src/app/shared/models/preference";
import { ColumnPreferenceTab } from "../column-preference.component";
import { ColumnService } from "src/app/shared/services/column-services/column.service";
import { KPIColumnService } from "src/app/modules/home/dashboard/Service/kpi-column.service";
import { TaskbankColumnService } from "src/app/modules/home/dashboard/task-bank/column-service/taskbank-column.service";
import { takeLast } from "rxjs";

const initialState: ColumnPreference = {
  name: '',
  description: '',
  id: '',
  columnList: [],
  createdDate: '',
  type: ''
};

@Injectable({
  providedIn: "root",
})

export class UserColumnPreferencesService {
  /**
   * Reactive signal to hold the list of columns.
   * Initialized as an empty array of ColumnList objects.
   * This represents the columns available or selected for user preferences.
   */
  columnList = signal<ColumnList[]>([]);

  /**
   * Reactive signal to control the visibility of the preference form.
   * `true` shows the form, `false` hides it.
   */
  showPreferenceForm = signal<boolean>(false);

  /**
   * Reactive signal to hold the current title of the preferences tab.
   * (e.g., "KPI", "TaskBank", "MainKPI") of preferences being shown.
   */
  title = signal<string>("");
  
  userPreferenceForm = signal<ColumnPreference>({...initialState});

  showToaster = signal<{showToaster: boolean | null, message: string,error:boolean | null}>({
    showToaster:null,
    message:"",
    error: null
  })



  constructor(
    private readonly userPreferenceService: UserPreferencesService,
    private readonly dataRepositoryService: DataRepositoryService,
    private readonly columnService: ColumnService,
    private readonly kpiColumnService: KPIColumnService,
    private readonly taskBankColumnService: TaskbankColumnService
  ) {}

  /**
   * Adds a new preference by:
   * - Setting the title of the preference tab.
   * - Setting the list of columns to be displayed/edited.
   * - Showing or hiding the preference form based on the provided flag.
   *
   * @param columnList - Array of column configurations to display.
   * @param title - Title of the preference section (e.g., 'KPI','TaskBank', 'MainKPI').
   * @param showPreferenceForm - Whether to show the preference form.
   */
  addNewPreference(
    columnList: ColumnList[],
    title: string,
    showPreferenceForm: boolean,
    preferencePayload?: ColumnPreference
  ) {
    this.title.set(title);
    this.columnList.set(columnList);
    this.showHidePreferenceForm(showPreferenceForm);
    this.userPreferenceForm.set({...initialState})
    if(preferencePayload) {
      this.userPreferenceForm.set(preferencePayload)
    }
  }

  /**
   * Shows or hides the preference form based on the provided boolean flag.
   *
   * @param showPreferenceForm - `true` to show the form, `false` to hide it.
   */
  showHidePreferenceForm(showPreferenceForm: boolean) {
    this.showPreferenceForm.set(showPreferenceForm);
  }

  /**
   * To get the Column Preferences set for the
   * TASKBANK | KPI | MAINKPI
   * for the respective user
   * @returns Observable response from backend service.
   */
  getUserColumnPreference() {
    this.userPreferenceService
      .getUserPreferenceByKey(UserPreferenceEnum.USERCOLUMNPREFERENCES).pipe(takeLast(1))
      .subscribe({
        next: (response: PreferenceResponseModel) => {
          let responseBody = response?.preferenceValue
            ? JSON.parse(response?.preferenceValue)
            : [];
          responseBody.push(this.taskBankColumnService.generateDefaultColumnList());
          responseBody.push(this.kpiColumnService.generateDefaultColumnList(ColumnPreferenceTab.KPI))
          responseBody.push(this.kpiColumnService.generateDefaultColumnList(ColumnPreferenceTab.MAINKPI))
          this.dataRepositoryService.userColumnPreferencesList.update(()=>[...responseBody]);
        },
        error: (error: any) => {
          this.dataRepositoryService.userColumnPreferencesList.set([]);
          this.showToaster.set({
            showToaster: true,
            message: ErrorMessage.GETUSERCOLUMNPREFERENCE,
            error: true
          })
        },
      });
  }

  /**
   * Synchronizes the existing column list with a predefined list of columns.
   * 
   * This method ensures that all columns from the predefined list are present
   * in the existing column list. If any predefined column is missing in the
   * existing list, it will be added with the `enable` property set to `true`.
   * Existing columns keep their original properties.
   * 
   * After adding missing columns, the combined list is sorted based on the
   * `order` property to maintain a consistent column order.
   * 
   * @param {ColumnList[]} columnList - The existing list of column objects.
   * @param {ColumnList[]} predefinedList - The predefined list of column objects which may include new columns.
   * @returns {ColumnList[]} A new array of columns containing all original columns plus any missing predefined columns added with `enable: true`, sorted by `order`.
   */
  syncColumnLists(columnList:ColumnList[], predefinedList: ColumnList[]) {
    const existingIds = new Set(columnList.map(col => col.id));
    let updatedColumnList = [...columnList];
    predefinedList.forEach(predefCol => {
      if (!existingIds.has(predefCol.id)) {
        updatedColumnList.push({ ...predefCol, enable: true });
      }
    });
    updatedColumnList.sort((a, b) => a.order - b.order);
    return updatedColumnList;
  }

  /**
   * To save user column preferecne 
   * added for MAINKPI, KPI , TASK BANK
   * @param columnPreference is structure to save column preference
   *  name: string;
      id: string;
      columnList: ColumnList[];
      createdDate: string;
      description: string;
    *
   }
   */
  saveUserColumnPreference(columnPreference: ColumnPreference) {
    this.userPreferenceService
      .saveUserPreference(UserPreferenceEnum.USERCOLUMNPREFERENCES, columnPreference)
      .subscribe({
        next: (response: PreferenceResponseModel) => {
          this.getUserColumnPreference();
          if (columnPreference.id) {
            this.showToaster.set({
              showToaster: true,
              message: ErrorMessage.SUCCESSUPDATEUSERCOLUMNPREFERENCE,
              error: false
            });
            this.setExistingUserColumnPreference(columnPreference)
          } else {
            this.showToaster.set({
              showToaster: true,
              message: ErrorMessage.SUCCESSCREATEDUSERCOLUMNPREFERENCE,
              error: false
            })
          }
          this.showPreferenceForm.set(false)
        },
        error: (error: any) => {
          this.showToaster.set({
            showToaster: true,
            message: ErrorMessage.ERRORSAVEUSERCOLUMNPREFERENCE,
            error: true
          })
        },
      });
  }

  setExistingUserColumnPreference(columnPreference: ColumnPreference) {
    const currentPrefs = this.dataRepositoryService.selectedUserColumnPreferences();
    const newPrefs: SelectedColumnPreferences = currentPrefs ? { ...currentPrefs } : {};
    switch (columnPreference?.type) {
      case ColumnPreferenceTab.TASKBANK.toLowerCase():
        if(newPrefs[SelectedPreferencesENUM.TASKBANK]?.id === columnPreference.id) {
          newPrefs[SelectedPreferencesENUM.TASKBANK] = columnPreference;
        }
      break;
      case ColumnPreferenceTab.MAINKPI.toLowerCase():
        if(newPrefs[SelectedPreferencesENUM.MAINKPI]?.id === columnPreference.id) {
          newPrefs[SelectedPreferencesENUM.MAINKPI] = columnPreference;
        }
      break;
      case ColumnPreferenceTab.KPI.toLowerCase():
        if(newPrefs[SelectedPreferencesENUM.KPI]?.id === columnPreference.id) {
          newPrefs[SelectedPreferencesENUM.KPI] = columnPreference;
        }
      break;
    }
    this.dataRepositoryService.selectedUserColumnPreferences.set(newPrefs);
    this.dataRepositoryService.tempSelectedUserColumnPreferences.set(newPrefs);
    this.columnService.saveUserPreference(newPrefs);
  }

  /**
   * 
   * @param id 
   */
  deleteColumnPreference(id:string) {
    const payload: DeleteListPaylaod = {
      preferenceKey: UserPreferenceEnum.USERCOLUMNPREFERENCES,
      id: id,
    };
    this.userPreferenceService.deleteUserPreference(payload).subscribe({
      next: (resp: PreferenceResponseModel) => {
        this.showToaster.set({
          showToaster: true,
          message: ErrorMessage.DELETEDCOLUMNPREFERENCE,
          error: false
        })        
        this.getUserColumnPreference();
        this.showPreferenceForm.set(false);
        const currentPrefs = this.dataRepositoryService.selectedUserColumnPreferences() || {};   
        const key = Object.keys(currentPrefs).find( key => currentPrefs[key].id === payload.id)
        
        if(key) {
          delete currentPrefs[key];
          this.dataRepositoryService.selectedUserColumnPreferences.update(()=> currentPrefs);
          this.dataRepositoryService.tempSelectedUserColumnPreferences.update(()=> currentPrefs);
          this.columnService.saveUserPreference(currentPrefs);
        }
      },
      error: (err: any) => {
        this.showToaster.set({
          showToaster: true,
          message: 'Network Error! Please try again.',
          error: true
        })  
      },
    });
  }
}
